import { applyMode as applyCloudscapeMode, Mode as CloudscapeMode } from "@cloudscape-design/global-styles";
import { applyMode as applyAwsuiMode, Mode as AwsuiMode } from "@amzn/awsui-global-styles";
import { getDebugSettings } from "@amzn/awsmp-max-agentic-renderer";
import { isDarkModeEnabled } from "../featureFlags/darkModeFeatureFlag";
import { isThemePreference, useThemePreferenceStore, type ThemePreference } from "./themePreferenceStore";

const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

export function subscribeToSystemScheme(onChange: () => void): () => void {
  const query = window.matchMedia?.(DARK_SCHEME_QUERY);
  query?.addEventListener("change", onChange);
  return () => query?.removeEventListener("change", onChange);
}

export function systemPrefersDark(): boolean {
  return window.matchMedia?.(DARK_SCHEME_QUERY).matches ?? false;
}

export interface ColorModeInputs {
  stored: unknown;
  debugDarkMode: boolean;
  prefersDark: boolean;
}

export function resolveColorMode({ stored, debugDarkMode, prefersDark }: ColorModeInputs): { preference: ThemePreference; isDark: boolean } {
  const choice = isThemePreference(stored) ? stored : null;
  const preference = choice ?? (isDarkModeEnabled(debugDarkMode) ? "system" : "light");
  return { preference, isDark: preference === "dark" || (preference === "system" && prefersDark) };
}

const VISUAL_REFRESH_SCOPE = ".awsui-visual-refresh";

function markDarkScope(scope: Element): void {
  if (scope.matches(VISUAL_REFRESH_SCOPE) && !scope.classList.contains("awsui-polaris-dark-mode")) applyAwsuiMode(AwsuiMode.Dark, scope);
}

let darkScopeObserver: MutationObserver | undefined;

const stopMarkingDarkScopes = () => darkScopeObserver?.disconnect();

/**
 * Polaris reads its dark tokens from `.awsui-polaris-dark-mode.awsui-visual-refresh` on one element, so while dark
 * every visual-refresh scope carries the mode class as well as `<body>`. The scopes stay off `<body>` because the
 * Marketplace chrome keys its own tokens on `.awsui-visual-refresh`, and a scope there restyles it in light mode.
 * Returns a function that stops marking scopes mounted afterwards.
 */
export function applyColorMode(isDark: boolean): () => void {
  applyCloudscapeMode(isDark ? CloudscapeMode.Dark : CloudscapeMode.Light);
  applyAwsuiMode(isDark ? AwsuiMode.Dark : AwsuiMode.Light);
  stopMarkingDarkScopes();
  const scopes = document.querySelectorAll(VISUAL_REFRESH_SCOPE);
  if (!isDark) {
    scopes.forEach((scope) => applyAwsuiMode(null, scope));
    return stopMarkingDarkScopes;
  }
  scopes.forEach(markDarkScope);
  // Catches scopes that mount later, such as portalled modals, and re-renders that rewrite a scope's className.
  darkScopeObserver ??= new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === "attributes" && record.target instanceof Element) markDarkScope(record.target);
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) [node, ...node.querySelectorAll(VISUAL_REFRESH_SCOPE)].forEach(markDarkScope);
      });
    }
  });
  darkScopeObserver.observe(document.body, { subtree: true, childList: true, attributeFilter: ["class"] });
  return stopMarkingDarkScopes;
}

const THEME_PARAM = "max_theme";
const CLEAR_THEME = "default";

/**
 * With `max_debug=true` in the same URL, `max_theme=light|dark|system` stores that choice and `max_theme=default` clears
 * it. Either may sit in the query or after the `#/route`, as HashRouter puts it. `max_theme` is removed from the URL
 * whether or not it applied, so a copied link does not carry it.
 */
function applyThemeFromUrl(): void {
  const { hash: rawHash } = window.location;
  const hashQueryStart = rawHash.indexOf("?");
  const hashPath = hashQueryStart === -1 ? rawHash : rawHash.slice(0, hashQueryStart);
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(hashQueryStart === -1 ? "" : rawHash.slice(hashQueryStart + 1));
  const value = search.get(THEME_PARAM) ?? hash.get(THEME_PARAM);
  if (value === null) return;
  const gateOpen = (search.get("max_debug") ?? hash.get("max_debug")) === "true";

  const url = new URL(window.location.href);
  if (search.has(THEME_PARAM)) {
    search.delete(THEME_PARAM);
    url.search = search.toString();
  }
  if (hash.has(THEME_PARAM)) {
    hash.delete(THEME_PARAM);
    const hashParams = hash.toString();
    url.hash = `${hashPath}${hashParams ? `?${hashParams}` : ""}`;
  }
  window.history.replaceState(window.history.state, "", url.href);

  if (!gateOpen) return;
  try {
    if (isThemePreference(value)) useThemePreferenceStore.getState().setPreference(value);
    else if (value === CLEAR_THEME) useThemePreferenceStore.getState().setPreference(null);
  } catch (error) {
    console.warn("max_theme: the theme choice applies to this page only, because saving it failed", error);
  }
}

/** Applies the mode from the URL and the persisted stores before React mounts, so the boot screen is already in the right mode. */
export function applyInitialColorMode(): void {
  applyThemeFromUrl();
  const { isDark } = resolveColorMode({
    stored: useThemePreferenceStore.getState().preference,
    debugDarkMode: getDebugSettings().darkMode,
    prefersDark: systemPrefersDark(),
  });
  applyColorMode(isDark);
}
